'use client'

import Image from 'next/image'
import Link from 'next/link'
import FullBleedSection from '@/components/FullBleedSection'
import { motion } from 'framer-motion'
import PhotovoltaikFAQ from '@/components/PhotovoltaikFAQ'
import PhotovoltaicSection from '@/components/PhotovoltaicSection'
import PricingFrame from '@/components/PricingPhotovoltaic'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import LazyPricingTable from './components/LazyPricingTable'
import LazyPricingSection from './components/LazyPricingSection'
import AIOptimizedSchema from '@/components/AIOptimizedSchema'
import HowItWorksSection from './HowItWorksSection'

// Performance monitoring for development
const PerformanceMonitor = dynamic(() => import('@/components/PerformanceMonitor'), {
  ssr: false,
})

// Create client-side wrapper for animations only - lazy loaded to reduce initial JS
const ClientMotionWrapper = dynamic(() => import('@/components/ClientMotionWrapper'), {
  ssr: false,
  loading: () => <div className="contents" />,
})

// AI-optimized schema data for Photovoltaik service
const photovoltaikSchemaData = {
  name: 'Volks-Photovoltaik',
  description:
    'Deutschlands günstigste Photovoltaikanlage: 9 kWp + 10.66 kWh Speicher für nur 13.900 €. Maximieren Sie Ihre Energieunabhängigkeit mit hochwertigen Komponenten.',
  url: 'https://godeutschland.de/photovoltaik',
  price: '13900',
  category: 'Solarenergie',
  features: [
    '9 kWp Leistung',
    '10.66 kWh Speicher',
    '450 Wp Glas-Glas-Module',
    'Bifazial, Black Frame',
    'Lithium Eisenphosphat Speicher',
    '11 kW Wallbox',
    'App-Steuerung',
    'Deutsche Qualität',
  ],
  benefits: [
    'Maximale Energieunabhängigkeit',
    'Kosteneffiziente Lösung',
    'Hochwertige Komponenten',
    'Optimale Autarkie',
    'Intelligente Steuerung',
  ],
  breadcrumbs: [
    { name: 'Home', url: 'https://godeutschland.de' },
    { name: 'Photovoltaik', url: 'https://godeutschland.de/photovoltaik' },
  ],
  faqs: [
    {
      question: 'Wie viel kostet die Volks-Photovoltaikanlage?',
      answer: 'Die komplette Anlage mit 9 kWp + 10.66 kWh Speicher kostet 13.900 €.',
    },
    {
      question: 'Was ist im Preis enthalten?',
      answer:
        'Im Preis sind enthalten: 9 kWp Solarmodule, 10.66 kWh Lithium-Speicher, 11 kW Wallbox, Installation und App-Steuerung.',
    },
    {
      question: 'Wie hoch ist die Leistung der Solarmodule?',
      answer:
        'Die hochwertigen Glas-Glas-Module haben jeweils 450 Wp Leistung und sind bifazial mit schwarzem Rahmen.',
    },
    {
      question: 'Welche Art von Speicher wird verwendet?',
      answer:
        'Wir verwenden einen sicheren Lithium Eisenphosphat Speicher mit 12,5 kWh für optimale Sicherheit und Leistung.',
    },
  ],
}

export default function PhotovoltaikPage() {
  return (
    <>
      {/* AI-Optimized Schema Markup for Photovoltaik Service */}
      <AIOptimizedSchema
        type="service"
        serviceData={{
          name: 'Volks-Photovoltaik',
          description:
            '9 kWp + 10.66 kWh Speicher für nur 13.900 €. Maximieren Sie Ihre Energieunabhängigkeit',
          price: '13900',
          category: 'Solarenergie',
          features: ['9 kWp Leistung', '10.66 kWh Speicher', '11 kW Wallbox', 'Deutsche Qualität'],
        }}
      />

      <Head>
        {/* Resource hints for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://connect.facebook.net" />
        {/* Critical images only */}
        <link rel="preload" href="/images/desktop-main.webp" as="image" />
        <link
          rel="preload"
          href="/images/photovoltaik-hero-mobile.webp"
          as="image"
          media="(max-width: 768px)"
        />
      </Head>
      <div>
        {/* Hero Image Section */}
        <div className="relative min-h-screen"></div>
        <div className="absolute inset-0 -z-10">
          <Image
            src="/images/desktop-main.webp"
            alt="Photovoltaik Anlage"
            quality={100}
            fill
            priority
            fetchPriority="high"
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>
        {/* Mobile Image */}
        <div className="absolute inset-0 -z-10 block md:hidden">
          <Image
            src="/images/photovoltaik-hero-mobile.webp"
            alt="Photovoltaik Anlage Mobile"
            quality={100}
            fill
            priority
            fetchPriority="high"
            className="object-cover object-center"
            sizes="(max-width: 768px) 100vw, 0px"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Hero Text */}
        <div className="absolute inset-0 z-10">
          <div className="relative z-10 mx-auto max-w-7xl">
            <div className="flex min-h-screen flex-col px-4 pt-32 sm:block sm:px-6 md:px-8 lg:px-10">
              {/* Hero Content */}
              <div className="flex flex-grow flex-col justify-center pb-8 text-center sm:absolute sm:inset-0 sm:mb-60 md:pl-0 md:text-left">
                <div>
                  <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
                    Die Volks-<span className="text-[#ff4500]">Photovoltaik</span>
                  </h1>
                  <p className="mb-4 text-xl text-white md:text-2xl">
                    9 kWp + 10.66 kWh Speicher = 13.900 €
                  </p>
                  {/* LCP Element - render immediately without animation delays */}
                  <p className="mb-8 text-lg text-white" style={{ willChange: 'auto' }}>
                    Deutschland günstigste Photovoltaikanlage: Maximieren Sie Ihre
                    Energieunabhängigkeit mit unseren hochwertigen und leistungsstarken Komponenten.
                    Effiziente Stromerzeugung kombiniert mit zuverlässiger Speichertechnologie mit
                    unserem GoDeutschland Speicher für optimale Autarkie.
                  </p>
                  <div className="mb-8 flex flex-wrap items-center justify-center gap-4 md:mb-0 md:justify-start">
                    <Link
                      href="#how-it-works"
                      className="inline-block cursor-pointer rounded-lg bg-white px-8 py-4 font-bold text-[#ff4500] transition-all hover:scale-105 hover:bg-gray-100"
                    >
                      Video ansehen
                    </Link>
                    <Link
                      href="/termin"
                      className="inline-block rounded-lg bg-[#ff4500] px-8 py-4 font-bold text-white transition-all hover:scale-105 hover:bg-[#e03e00]"
                    >
                      Jetzt Angebot anfordern
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <HowItWorksSection />
        {/* Benefits Section */}
        <FullBleedSection className="bg-white px-4 py-12 md:px-0 dark:bg-gray-900">
          <div className="mx-auto max-w-7xl">
            {/* First row - 3 boxes with different widths */}
            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-12">
              {/* Left Box */}
              <ClientMotionWrapper
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative col-span-1 flex flex-col items-center justify-center overflow-hidden rounded-md text-center text-white md:col-span-3"
                style={{ paddingTop: '35%', paddingBottom: '24px' }}
              >
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                  <Image
                    src="/images/solar-panel-copy.webp"
                    alt="Solar Panel Background"
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 25vw, 20vw"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                </div>
                {/* Content */}
                <div className="relative z-10">
                  <h2 className="mb-4 text-4xl font-bold">450 Wp</h2>
                  <p className="text-lg">
                    Hochwertige <br className="hidden md:block" /> {/* only on desktop/tablet */}
                    Glas-Glas-Module,
                    <br className="block md:hidden" /> {/* only on mobile */}
                    Bifazial, Black
                    <br className="hidden md:block" /> {/* only on desktop/tablet */}
                    Frame für optimale Leistung
                  </p>
                </div>
              </ClientMotionWrapper>

              {/* Middle Box */}
              <ClientMotionWrapper
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="col-span-1 flex flex-col rounded-md bg-[#e9e9e6] p-6 text-[#21262b] md:col-span-6"
              >
                <div className="flex h-full items-center justify-center text-center">
                  <div>
                    <h3 className="text-lg font-medium">Kostengünstige Insellösung</h3>
                    <h2 className="mt-2 text-4xl font-bold">kostengünstige Insellösungen mit</h2>
                    <p className="mt-4 text-lg">
                      Schwarzstartfähigkeit. Umschaltung in nur 0 ms ohne <br />
                      Spannungsunterbrechung
                    </p>
                  </div>
                </div>
              </ClientMotionWrapper>

              {/* Right Box */}
              <ClientMotionWrapper
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative col-span-1 flex flex-col items-center justify-center overflow-hidden rounded-md text-white md:col-span-3"
                style={{ paddingTop: '30%' }}
              >
                <div className="absolute inset-0 z-0">
                  <Image
                    src="/images/ev-charger.webp"
                    alt="EV Charger Background"
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 25vw, 20vw"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/50" />
                </div>
                <div className="relative z-10 w-full px-4 text-center">
                  <h2 className="text-4xl font-bold">Bidirektionale Wallbox</h2>
                  <p className="my-4">Bidirektionales Laden mit 25 kW</p>
                </div>
              </ClientMotionWrapper>
            </div>

            {/* Second row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              {/* Left column */}
              <div className="col-span-1 md:col-span-6">
                {/* Top Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mb-4 rounded-md bg-white p-6 text-center"
                >
                  <h2 className="text-4xl font-bold">
                    Vorteile Unserer <br className="block md:block" /> Volks-Photovoltaikanlage
                  </h2>
                </motion.div>

                {/* Bottom Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="relative overflow-hidden rounded-md p-10"
                >
                  <div className="absolute inset-0 z-0">
                    <video
                      src="/videos/instaling-solar.webm"
                      className="h-full w-full object-cover object-center"
                      autoPlay
                      loop
                      muted
                      playsInline // Add this to prevent fullscreen on iOS
                      preload="auto" // Add this for better loading
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30" />
                  </div>
                  <div className="relative z-10 flex h-full items-center justify-center">
                    <div className="text-center">
                      <h2 className="text-4xl font-bold text-white">Installation</h2>
                      <p className="text-xl text-white">
                        Bundesweite Installation und Inbetriebnahme inklusive
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Right column - 2 boxes */}
              <div className="col-span-1 md:col-span-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Box 1 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="flex flex-col items-center justify-center rounded-md bg-[#ff4500] p-8 text-center text-white"
                    style={{ paddingTop: '38px', paddingBottom: '38px' }}
                  >
                    <h2 className="mb-2 text-5xl font-bold">0 € Anzahlung</h2>
                    <p className="text-center">
                      Wir installieren bundesweit Ihre Volks-Photovoltaik.
                    </p>
                  </motion.div>

                  {/* Box 2 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="relative flex flex-col items-center justify-center overflow-hidden rounded-md bg-[#e9e9e6] text-[#21262b]"
                    style={{ paddingTop: '64px', paddingBottom: '68px' }}
                  >
                    <div className="text-center">
                      <h2 className="mb-2 text-4xl font-bold">
                        KI-Energie
                        <br />
                        management
                      </h2>
                      <p>
                        <br />
                        Intelligentes Laden und dynamische <br />
                        Stromtarife
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </FullBleedSection>

        {/* Instalation Section */}
        <FullBleedSection className="bg-[#e9e9e6] py-20 dark:bg-gray-900/50">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="px-4 text-center"
          >
            <span className="text-lg font-semibold tracking-wider text-[#ff4500] uppercase">
              Preisleistungsverhältnis
            </span>
            <h2 className="mt-6 text-center text-4xl font-bold text-gray-900 sm:text-4xl">
              Maximale <span className="text-[#ff4500]">Effizienz </span>dank innovativer
              Technologie
            </h2>
            <p className="mx-auto mt-6 max-w-4xl text-center text-lg text-gray-700">
              Die Leistung der Solarmodule ist entscheidend für die Effizienz und Wirtschaftlichkeit
              Ihrer Photovoltaikanlage. Ausgestattet mit modernster Technologie maximieren diese
              Module den Energieertrag und sorgen auch bei suboptimalen Wetterbedingungen für
              optimale Ergebnisse.
            </p>
          </motion.div>
        </FullBleedSection>

        <PhotovoltaicSection />
        <LazyPricingSection />

        {/* Pricing Table Section - Lazy Loaded */}
        <FullBleedSection className="bg-white pt-0 pb-16 dark:bg-gray-900">
          <LazyPricingTable />
        </FullBleedSection>
        <PricingFrame />
        <PhotovoltaikFAQ />

        {/* Development Tools */}
        <PerformanceMonitor />
      </div>
    </>
  )
}
